import {
  afterNextRender,
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  Injector,
  input,
  NgZone,
  OnDestroy,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { outputToObservable } from '@angular/core/rxjs-interop';
import { firstValueFrom, take } from 'rxjs';

import { GoogleMapComponent } from '../map/google-map.component';

@Component({
  selector: 'be-map-advanced-marker',
  template: `
    @if (initialized()) {
      <div #wrapper><ng-content /></div>
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MapAdvancedMarkerComponent implements AfterViewInit, OnDestroy {
  private readonly googleMap = inject(GoogleMapComponent);
  private readonly ngZone = inject(NgZone);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly injector = inject(Injector);

  id = input.required<string>();
  position = input<google.maps.LatLngLiteral>();
  markerClick = output<{
    marker: google.maps.marker.AdvancedMarkerElement;
  }>();
  markerInitialized = output<google.maps.marker.AdvancedMarkerElement>();
  private readonly wrapper = viewChild<ElementRef<HTMLDivElement>>('wrapper');

  private advancedMarker: google.maps.marker.AdvancedMarkerElement | undefined;

  initialized = signal(false);

  get advancedMarkerElement(): google.maps.marker.AdvancedMarkerElement | undefined {
    return this.advancedMarker;
  }

  ngAfterViewInit(): void {
    if (google.maps.marker?.AdvancedMarkerElement && this.googleMap.googleMap) {
      this.initialize(this.googleMap.googleMap, google.maps.marker.AdvancedMarkerElement);
    } else {
      Promise.all([this.googleMap.resolveMap(), google.maps.importLibrary('marker')]).then(
        ([map, lib]) => {
          this.initialize(map, (lib as google.maps.MarkerLibrary).AdvancedMarkerElement);
        },
      );
    }
  }

  ngOnDestroy(): void {
    this.removeFromMap();
  }

  removeFromMap(): void {
    if (!this.advancedMarker) {
      return;
    }

    this.advancedMarker.map = null;
  }

  resolveMarker(): Promise<google.maps.marker.AdvancedMarkerElement> {
    return this.advancedMarkerElement
      ? Promise.resolve(this.advancedMarkerElement)
      : firstValueFrom(outputToObservable(this.markerInitialized).pipe(take(1)));
  }

  private initialize(
    map: google.maps.Map,
    advancedMarkerConstructor: typeof google.maps.marker.AdvancedMarkerElement,
  ): void {
    afterNextRender(
      () => {
        this.initialized.set(true);
        this.cdr.detectChanges();

        this.ngZone.runOutsideAngular(() => {
          const wrapper = this.wrapper()?.nativeElement;
          if (!wrapper) {
            return;
          }

          this.advancedMarker = new advancedMarkerConstructor({
            map,
            content: wrapper,
            position: this.position(),
          });

          this.advancedMarker.addListener('click', () => {
            if (this.advancedMarker) {
              this.markerClick.emit({ marker: this.advancedMarker });
            }
          });
          this.markerInitialized.emit(this.advancedMarker);
        });
      },
      { injector: this.injector },
    );
  }
}
